import { useSignalR } from '../components/providers/SignalRProvider.vue'
import type { SignalRReceiveEvent, SubscribeToEventOptions } from '../store'
import { ref } from 'vue'
import type {
  CustomFieldAddedSignalRPayload,
  CustomFieldUpdatedSignalRPayload,
  CustomFieldRemovedSignalRPayload,
  CustomFieldsOrderChangedSignalRPayload,
  CustomFieldValuesUpdatedSignalRPayload,
  FieldConfigChangedSignalRPayload
} from '@gat-ship/types/signal-r-events'
import { signalREvent } from '@gat-ship/types/signal-r-events'

export type FieldConfigurationEventHandlers = {
  onCustomFieldValuesUpdated?: (
    payload: SignalRReceiveEvent<CustomFieldValuesUpdatedSignalRPayload>
  ) => void
  onFieldConfigChanged?: (
    payload: SignalRReceiveEvent<FieldConfigChangedSignalRPayload>
  ) => void
  onCustomFieldAdded?: (
    payload: SignalRReceiveEvent<CustomFieldAddedSignalRPayload>
  ) => void
  onCustomFieldUpdated?: (
    payload: SignalRReceiveEvent<CustomFieldUpdatedSignalRPayload>
  ) => void
  onCustomFieldRemoved?: (
    payload: SignalRReceiveEvent<CustomFieldRemovedSignalRPayload>
  ) => void
  onCustomFieldsOrderChanged?: (
    payload: SignalRReceiveEvent<CustomFieldsOrderChangedSignalRPayload>
  ) => void
}

export function useFieldConfigurationSignalREvents(
  handlers?: FieldConfigurationEventHandlers
) {
  const { subscribe } = useSignalR()

  const subscription = ref<() => void>()

  function subscribeToCustomFieldValuesUpdated(
    cb: (
      payload: SignalRReceiveEvent<CustomFieldValuesUpdatedSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<CustomFieldValuesUpdatedSignalRPayload>(
      signalREvent.FieldConfiguration.V1.CustomFieldValuesUpdated,
      cb,
      options
    )
  }

  function subscribeToFieldConfigurationEvents() {
    if (subscription.value) {
      return subscription.value
    }

    if (handlers) {
      const unsubscribeFunctions: (() => void)[] = []

      if (handlers.onCustomFieldValuesUpdated) {
        unsubscribeFunctions.push(
          subscribeToCustomFieldValuesUpdated(
            handlers.onCustomFieldValuesUpdated
          )
        )
      }

      if (handlers.onFieldConfigChanged) {
        unsubscribeFunctions.push(
          subscribe<FieldConfigChangedSignalRPayload>(
            signalREvent.FieldConfiguration.V1.FieldConfigChanged,
            handlers.onFieldConfigChanged
          )
        )
      }

      if (handlers.onCustomFieldAdded) {
        unsubscribeFunctions.push(
          subscribe<CustomFieldAddedSignalRPayload>(
            signalREvent.FieldConfiguration.V1.CustomFieldAdded,
            handlers.onCustomFieldAdded
          )
        )
      }

      if (handlers.onCustomFieldUpdated) {
        unsubscribeFunctions.push(
          subscribe<CustomFieldUpdatedSignalRPayload>(
            signalREvent.FieldConfiguration.V1.CustomFieldUpdated,
            handlers.onCustomFieldUpdated
          )
        )
      }

      if (handlers.onCustomFieldRemoved) {
        unsubscribeFunctions.push(
          subscribe<CustomFieldRemovedSignalRPayload>(
            signalREvent.FieldConfiguration.V1.CustomFieldRemoved,
            handlers.onCustomFieldRemoved
          )
        )
      }

      if (handlers.onCustomFieldsOrderChanged) {
        unsubscribeFunctions.push(
          subscribe<CustomFieldsOrderChangedSignalRPayload>(
            signalREvent.FieldConfiguration.V1.CustomFieldsOrderChanged,
            handlers.onCustomFieldsOrderChanged
          )
        )
      }

      subscription.value = () => {
        unsubscribeFunctions.forEach((unsubscribe) => unsubscribe())
      }
    }

    return subscription.value
  }

  function unsubscribeFromFieldConfigurationEvents() {
    subscription.value?.()
    subscription.value = undefined
  }

  return {
    subscribeToFieldConfigurationEvents,
    unsubscribeFromFieldConfigurationEvents
  }
}
